import { useState, forwardRef } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { FilterPillBar } from "../FilterPillBar";

import { WatchLimitBlocker } from "../WatchLimitBlocker";
import { EmbeddablePlaylistViewModel, TagItem } from "../../ViewModel";
import EmbeddablePlayer from "../../../EmbeddablePlayer";
import { NoClips } from "../NoClips";

import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  PlayerPhotoState,
  TeamLogoState,
  FallbackPlaylistTypeState,
  ShowWatchLimitBlockerState,
  BlockedPlayerNameState,
  BlockedClipState,
  RenderPreMatchState,
  TopViewedPlayersState,
  TopViewedMatchesState,
  TopViewedTeamsState,
  DisplayEmptyState,
  TopViewedPlayersByCountryState,
} from "MVVM/State/embeddablePlaylist";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import { TopViewedListContainer } from "../TopViewedListContainer";
import { EmptyState } from "../HLTVEmbeddablePlaylist/Components/EmptyState";
import { MoreClipsColumn } from "./Components/MoreClipsColumn";

export const HLTVMobileEmbeddablePlaylist = forwardRef<
  HTMLDivElement,
  {
    platform: string;
    playlist: string;
    theme?: string;
    handleMouseEnter: () => void;
  }
>(({ platform, playlist, theme = "night", handleMouseEnter }, ref) => {
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const showWatchLimitBlocker = useRecoilValue(ShowWatchLimitBlockerState);
  const blockedPlayerName = useRecoilValue(BlockedPlayerNameState);
  const blockedClip = useRecoilValue(BlockedClipState);
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playerPhoto = useRecoilValue(PlayerPhotoState);
  const teamLogo = useRecoilValue(TeamLogoState);
  const displayEmptyState = useRecoilValue(DisplayEmptyState);
  const fallbackPlaylistType = useRecoilValue(FallbackPlaylistTypeState);
  const renderPreMatchState = useRecoilValue(RenderPreMatchState);
  const topViewedPlayers = useRecoilValue(TopViewedPlayersState);
  const topViewedPlayersByCountry = useRecoilValue(
    TopViewedPlayersByCountryState,
  );
  const topViewedMatches = useRecoilValue(TopViewedMatchesState);
  const topViewedTeams = useRecoilValue(TopViewedTeamsState);
  const renderTopViewedComponent = !!topViewedPlayers && !!topViewedMatches;
  const renderTopViewedList = new URLSearchParams(window.location.search).get(
    "topViewedList",
  );

  const { handleWatchLimitReached, handleCloseBlocker } = useWatchLimit();

  const { filters, getClipsForTag } = EmbeddablePlaylistViewModel({
    playlist,
    platform,
  });

  const filterPills = filters.map((tag: TagItem) => ({
    label: tag.value,
    key: `${tag.key}:${tag.value}`,
    count: tag.clips.length,
  }));

  const allFilters = [
    { label: "All", key: "all", count: playlistClips?.length || 0 },
    ...filterPills,
    { label: "More", key: "more", count: 0, externalLink: true },
  ];

  const clipsToDisplay = getClipsForTag(selectedTag);

  if (displayEmptyState)
    return (
      <Flex flexDirection="column" width="100%">
        <StyledContainer
          $renderTopViewedComponent={true}
          $renderPreMatchState={renderPreMatchState}
        >
          <StyledGradientLayer
            className="gradient-layer"
            $renderTopViewedComponent={true}
          >
            <EmptyState />
          </StyledGradientLayer>
          {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
            <Box style={{ height: "150px" }}>
              <LoadingSkeleton height="100%" width="100%" />
            </Box>
          ) : (
            <TopViewedListContainer
              topViewedPlayers={topViewedPlayers}
              topViewedPlayersByCountry={topViewedPlayersByCountry}
              topViewedMatches={topViewedMatches}
              topViewedTeams={topViewedTeams}
              theme={theme}
            />
          )}
        </StyledContainer>
      </Flex>
    );
  return (
    <Flex flexDirection={"column"} style={{ height: "100%", width: "100%" }}>
      <StyledContainer
        $renderTopViewedComponent={renderTopViewedComponent}
        $renderPreMatchState={renderPreMatchState}
        onMouseEnter={handleMouseEnter}
      >
        <StyledGradientLayer
          className="gradient-layer"
          $renderTopViewedComponent={false}
        >
          <InnerRow>
            <FluidPlayerWrapper
              $backgroundURL={activePlaylistClip?.thumbnailStandardUrl}
            >
              {activePlaylistClip && (
                <EmbeddablePlayer
                  userRole="viewer"
                  onPlayEndVariant="nextUpPlaylist"
                  isInMobileExperiment={true}
                />
              )}
              {fallbackPlaylistType &&
                !["lastMatch", "teamClips"].includes(fallbackPlaylistType) && (
                  <NoClips />
                )}
              {!activePlaylistClip && !fallbackPlaylistType && (
                <PlayerLoadingSkeleton>
                  <LoadingSkeleton width="100%" height="100%" />
                </PlayerLoadingSkeleton>
              )}
              {showWatchLimitBlocker && (
                <WatchLimitBlocker
                  onClose={handleCloseBlocker}
                  playerName={blockedPlayerName}
                  clickedClip={blockedClip}
                  primaryColor="#4DBD94"
                  playlistId={playlist}
                />
              )}
            </FluidPlayerWrapper>

            <RightCell>
              <StyledPlayerTeamSection>
                {teamLogo && <StyledTeamImage src={teamLogo} />}
                {playerPhoto && <StyledPlayerImage src={playerPhoto} />}
              </StyledPlayerTeamSection>

              <FilterRow>
                {allFilters.length > 0 ? (
                  <FilterPillBar
                    filters={allFilters}
                    selected={selectedTag}
                    onSelect={setSelectedTag}
                  />
                ) : (
                  <FilterPillBarSkeleton>
                    <Flex style={{ gap: "8px" }}>
                      {Array.from({ length: 5 }, (_, i) => (
                        <LoadingSkeleton
                          key={i}
                          width={48}
                          height={22}
                          style={{ borderRadius: "20px" }}
                        />
                      ))}
                    </Flex>
                  </FilterPillBarSkeleton>
                )}
              </FilterRow>
              <MoreClipsColumn
                clips={clipsToDisplay}
                onWatchLimitReached={handleWatchLimitReached}
              />
            </RightCell>
          </InnerRow>
          {renderTopViewedList && (
            <Flex flexDirection="column" style={{ maxHeight: "27%" }}>
              {!topViewedPlayers || !topViewedMatches || !topViewedTeams ? (
                <StyledLoadingContainer>
                  <LoadingSkeleton height="100%" width="100%" />
                </StyledLoadingContainer>
              ) : (
                <TopViewedListContainer
                  topViewedPlayers={topViewedPlayers}
                  topViewedPlayersByCountry={topViewedPlayersByCountry}
                  topViewedMatches={topViewedMatches}
                  topViewedTeams={topViewedTeams}
                  theme={theme}
                />
              )}
            </Flex>
          )}
        </StyledGradientLayer>
      </StyledContainer>
    </Flex>
  );
});

const RightCell = styled(Box)`
  flex: 1;
  min-width: 0;
  position: relative;
  max-height: 60%;
`;

const StyledPlayerTeamSection = styled(Box)`
  align-items: center;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 60%;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const StyledTeamImage = styled(Image)`
  height: 70%;
  opacity: 0.75;
  position: absolute;
  top: 20px;
  transform: translate(15px, -20px);
  width: auto;
  z-index: 1;
`;

const StyledPlayerImage = styled(Image)`
  height: 90%;
  position: absolute;
  top: 25%;
  width: auto;
  z-index: 2;

  -webkit-mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
`;

const FilterRow = styled(Box)`
  align-items: center;
  display: flex;
  flex: 0 0 100%;
  justify-content: center;
  padding-top: 5px;
`;

const FilterPillBarSkeleton = styled(Box)``;

const PlayerLoadingSkeleton = styled(Box)`
  border-radius: 8px;
  height: 100%;
  width: 100%;
`;

const StyledGradientLayer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
}>`
  backdrop-filter: blur(6px);
  background: linear-gradient(
      227.35deg,
      rgba(49, 113, 138, 0.3) -3.85%,
      rgba(99, 173, 146, 0.3) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.99), rgba(0, 0, 0, 0.99));
  flex-direction: column;
  flex: 1;
  gap: 12px;
  height: 100%;
  min-height: 0;
  padding: 10px;
  width: 100%;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
    flex: 1;
  `}

  @media (max-width: 515px) {
    gap: 8px;
    padding: 8px;
  }

  @media (max-width: 425px) {
    padding: 6px;
  }
`;

const StyledContainer = styled(Flex)<{
  $renderTopViewedComponent: boolean;
  $renderPreMatchState: boolean;
}>`
  background-image: url(${process.env
    .REACT_APP_S3_CLIP_BUCKET}/static/web/playlistBackgroundImage.jpg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-direction: column;
  height: 100vh;
  width: 100%;

  ${({ $renderTopViewedComponent }) =>
    $renderTopViewedComponent &&
    `
      flex: 1;
      gap: 0px;
      min-height: 0;
    `}
`;

const FluidPlayerWrapper = styled(Box)<{ $backgroundURL: string | undefined }>`
  aspect-ratio: 9/16;
  border-radius: 8px;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;

  .video-js {
    aspect-ratio: 9 / 16;
    background: transparent;
    height: 100% !important;
  }
  .vjs-tech {
    aspect-ratio: 9 / 16;
    border-radius: 8px;
    height: 100% !important;
  }

  ${({ $backgroundURL }) =>
    $backgroundURL &&
    `
      background-image: url(${$backgroundURL});
      background-size: 3000%;
      background-repeat: no-repeat;
      background-position: center;

      ::before {
        background-color: rgba(0, 0, 0, 0.6);
        content: "";
        display: block;
        height: 100%;
        left: 0px;
        position: absolute;
        top: 0px;
        width: 100%;
        z-index: 0;
      }
    `}
`;

const InnerRow = styled(Flex)`
  display: flex;
  flex: 1;
  gap: 12px;
  min-height: 0;
  min-width: 0;
  width: 100%;
`;

const StyledTopViewedColumn = styled(Box)`
  align-items: end;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
`;

const StyledLoadingContainer = styled(Box)`
  height: 180px;

  @media (max-width: 502px) {
    height: 150px;
  }

  @media (max-width: 380px) {
    height: 120px;
  }
`;
